import React, { useEffect, useState, useCallback } from "react";
import Skeleton from "../common/Skeleton";
import {
  Plus,
  ChevronUp,
  ChevronDown,
  Save,
  Check,
  CircleMinus,
  CircleCheck,
} from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import FormControl from "../common/FormControl";
import { ValidatedTextarea } from "../../validation/ValidatedFields";
import { useFieldValidation } from "../../validation/useFieldValidation";
import Toast, { ToastMessage } from "../common/Toast";
import Pagination from "../common/Pagination";
import StatusSwitch from "../common/StatusSwitch";
import { confirmWarning, confirmActive, confirmDelete, swalPermission } from "../common/confirm";
import controlImplementationsService, {
  ImplementationStep,
  ImplementationType,
  CreateImplPayload,
} from "../../services/controlImplementationsService";
import apiClient from "../../services/apiService";
import itAssetsService, { ITAsset } from "../../services/itAssetsService";
import applicationsService, {
  DBApplication,
} from "../../services/applicationsService";
import extractApiError from "../../utils/apiError";
import { usePermission, NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";

const TYPES: Array<{ value: ImplementationType; label: string }> = [
  { value: "SOP", label: "SOP" },
  { value: "IT_APPLICATION", label: "IT Application" },
  { value: "IT_ASSET", label: "IT Asset" },
];

interface TargetOption {
  id: number;
  title: string;
  subtitle?: string;
}

const ControlImplementationsTab: React.FC<{ controlId: number; canModify?: boolean }> = ({
  controlId,
  canModify: canModifyProp,
}) => {
  const { allowed: canModifyPerm, loading: permLoading } = usePermission('CTR_CONTROL_LIBRARY', 'view+modify');
  const canModify = (canModifyProp ?? true) && canModifyPerm;
  const fieldVh = useFieldValidation();
  const [steps, setSteps] = useState<ImplementationStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Toolbar filter — list shows either active or inactive steps. GET returns
  // both (is_active != -1), so we filter client-side then paginate.
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');

  // Modal state for adding/editing one step
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStep, setEditingStep] = useState<ImplementationStep | null>(
    null,
  );
  const [formType, setFormType] = useState<ImplementationType>("SOP");
  const [formTargetId, setFormTargetId] = useState<number | "">("");
  const [formExplanation, setFormExplanation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);

  // Target option lists, lazy-loaded by type
  const [sopOptions, setSopOptions] = useState<TargetOption[] | null>(null);
  const [appOptions, setAppOptions] = useState<TargetOption[] | null>(null);
  const [assetOptions, setAssetOptions] = useState<TargetOption[] | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setToast(null);
    try {
      const data = await controlImplementationsService.list(controlId);
      const wantActive = statusFilter === 'ACTIVE';
      const filtered = data.filter((s) => (s.is_active === 1) === wantActive);
      setTotalCount(filtered.length);
      const startIdx = (page - 1) * pageSize;
      const paginatedData = filtered.slice(startIdx, startIdx + pageSize);
      setSteps(paginatedData);
    } catch (e: any) {
      setToast({
        type: "error",
        message: extractApiError(e, "Failed to load implementations"),
      });
    } finally {
      setLoading(false);
    }
  }, [controlId, page, pageSize, statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Load target options for the active form type
  useEffect(() => {
    if (!showAddModal) return;
    let cancelled = false;
    const load = async () => {
      try {
        if (formType === "SOP" && !sopOptions) {
          // Approved + active SOPs only via unified /dropdown.
          const r = await apiClient.get("/api/v1/gov/processes/dropdown");
          const items = (r.data?.items ?? []) as Array<{ id: number; name: string }>;
          if (!cancelled)
            setSopOptions(
              items.map((p) => ({
                id: p.id,
                title: p.name,
              })),
            );
        } else if (formType === "IT_APPLICATION" && !appOptions) {
          const apps: DBApplication[] = (await applicationsService.getAll({
            status: "ACTIVE",
            limit: 100,
          })).filter((a) => a.workflow_state === "approved");
          if (!cancelled)
            setAppOptions(
              apps.map((a) => ({
                id: a.id,
                title: a.application_name,
                subtitle: a.application_code,
              })),
            );
        } else if (formType === "IT_ASSET" && !assetOptions) {
          const assets: ITAsset[] = (await itAssetsService.list({
            status: "ACTIVE",
          })).filter((a) => a.workflow_state === "approved");
          if (!cancelled)
            setAssetOptions(
              assets.map((a) => ({
                id: a.id,
                title: a.asset_name,
                subtitle: `${a.asset_code} · ${a.asset_type}`,
              })),
            );
        }
      } catch (e) {
        if (!cancelled)
          setToast({
            type: "error",
            message: extractApiError(e, "Failed to load options"),
          });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [showAddModal, formType, sopOptions, appOptions, assetOptions]);

  const currentOptions: TargetOption[] =
    (formType === "SOP"
      ? sopOptions
      : formType === "IT_APPLICATION"
        ? appOptions
        : assetOptions) || [];

  const openAdd = () => {
    if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
    setEditingStep(null);
    setFormType("SOP");
    setFormTargetId("");
    setFormExplanation("");
    setSubmitting(false);
    setToggling(false);
    fieldVh.reset();
    setShowAddModal(true);
  };

  const openEdit = (s: ImplementationStep) => {
    if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
    setEditingStep(s);
    setFormType(s.implementation_type);
    setFormTargetId(s.target_id);
    setFormExplanation(s.explanation || "");
    setSubmitting(false);
    setToggling(false);
    fieldVh.reset();
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingStep(null);
    setSubmitting(false);
    setToggling(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allValid = fieldVh.validateAll([
      { name: 'explanation', code: 110, value: formExplanation || '' },
    ]);
    if (!allValid) return;
    if (formTargetId === "") {
      setToast({ type: "error", message: "Please pick a target" });
      return;
    }
    setSubmitting(true);
    setToast(null);
    try {
      if (editingStep) {
        await controlImplementationsService.update(controlId, editingStep.id, {
          implementation_type: formType,
          target_id: Number(formTargetId),
          explanation: formExplanation || undefined,
        });
        setToast({ type: "success", message: "Step updated successfully." });
      } else {
        const payload: CreateImplPayload = {
          implementation_type: formType,
          target_id: Number(formTargetId),
          explanation: formExplanation || undefined,
        };
        await controlImplementationsService.create(controlId, payload);
        setToast({ type: "success", message: "Step created successfully." });
        setPage(1);
      }
      closeModal();
      await refresh();
    } catch (e: any) {
      setToast({ type: "error", message: extractApiError(e, "Save failed") });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (s: ImplementationStep) => {
    const ok = await confirmDelete({
      title: `Remove implementation step #${s.sequence_number}?`,
      text: `"${s.target_label}" will be removed from this control.`,
    });
    if (!ok) return;
    setSubmitting(true);
    setToast(null);
    try {
      await controlImplementationsService.remove(controlId, s.id);
      setToast({ type: "delete", message: "Step deleted successfully" });
      setPage(1);
      await refresh();
    } catch (e: any) {
      setToast({ type: "error", message: extractApiError(e, "Delete failed") });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
    if (!editingStep) return;
    const next = editingStep.is_active === 1 ? 0 : 1;
    const isActive = editingStep.is_active === 1;
    const confirmFn = isActive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this step?" : "Inactivate this step?",
      text: next
        ? "It will be included in this control again."
        : "It will be hidden from this control.",
      confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    setToast(null);
    try {
      await controlImplementationsService.update(controlId, editingStep.id, {
        is_active: next,
      });
      setToast({
        type: next ? "success" : "warning",
        message: next ? "Reactivated" : "Inactivated",
      });
      closeModal();
      await refresh();
    } catch (e: any) {
      setToast({
        type: "error",
        message: extractApiError(e, "Failed to update status"),
      });
    } finally {
      setToggling(false);
    }
  };

  const handleMove = async (
    s: ImplementationStep,
    direction: "UP" | "DOWN",
  ) => {
    if (!canModify) { swalPermission('You do not have permission to modify this record.'); return; }
    setToast(null);
    try {
      await controlImplementationsService.move(controlId, s.id, direction);
      await refresh();
      setToast({ type: "success", message: "Step reordered." });
    } catch (e: any) {
      setToast({
        type: "error",
        message: extractApiError(e, "Reorder failed"),
      });
    }
  };

  const typeBadgeClass = (t: ImplementationType) => {
    switch (t) {
      case "SOP":
        return "status-badge xs info";
      case "IT_APPLICATION":
        return "status-badge xs info";
      case "IT_ASSET":
        return "status-badge xs warning";
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Description paragraph */}
      {/* <p className="text-sm text-gray-500 flex-shrink-0">
        Define how this control is implemented — through SOP procedure steps, IT
        applications, and/or IT assets.
      </p> */}

      {/* Toolbar (netbody-header) */}
      <div className="netbody-header">
        <StatusSwitch
          value={statusFilter}
          onChange={(next) => { setStatusFilter(next); setPage(1); }}
          noun="steps"
          testId="BTN_control_impl_status_filter"
          tipPlacement="right"
        />
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="fill" icon={Plus} onClick={openAdd} disabled={permLoading || !canModify} title={!canModify ? NOT_AUTHORISED_MSG : undefined} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_control_impl_create">
            Create
          </Button>
        </div>
      </div>

      {/* Steps table */}
      <div className="table-list flex-1 min-h-0">
        <div className="table-list-scroll">
          <table className="table-list-table">
            <thead>
              <tr>
                <th className="table-list-th table-list-cell--sno">S.No</th>
                <th className="table-list-th">Type</th>
                <th className="table-list-th">Target</th>
                <th className="table-list-th">Explanation</th>
                <th className="table-list-th table-list-th--right">Actions</th>
              </tr>
            </thead>
            <tbody aria-busy={loading} aria-live="polite">
              {loading && <Skeleton.TableRows cols={['3xs', 'pill', 'md', 'md', { width: 'icon', align: 'right', count: 2 }]} />}
              {!loading && steps.length === 0 && (
                <tr>
                  <td colSpan={5} className="table-list-empty-cell">
                    {statusFilter === 'INACTIVE' ? (
                      "No inactive implementation steps."
                    ) : (
                      <>
                        No implementation steps yet. Click{" "}
                        <strong>+ Create Step</strong> to define how this control
                        works.
                      </>
                    )}
                  </td>
                </tr>
              )}
              {!loading &&
                steps.map((s, idx) => (
                  <tr
                    key={s.id}
                    className="table-list-row cursor-pointer"
                    onClick={() => openEdit(s)}
                    data-testid={`BTN_control_impl_row_${s.id}_open`}
                  >
                    <td className="table-list-cell table-list-cell--sno">
                      {s.sequence_number}
                    </td>
                    <td className="table-list-cell">
                      <span
                        className={typeBadgeClass(s.implementation_type)}
                      >
                        {s.implementation_type === "IT_APPLICATION"
                          ? "IT App"
                          : s.implementation_type === "IT_ASSET"
                            ? "IT Asset"
                            : s.implementation_type}
                      </span>
                    </td>
                    <td className="table-list-cell">
                      <span className="table-list-cell-title">
                        {s.target_label}
                      </span>
                    </td>
                    <td
                      className="table-list-cell table-list-cell--truncate"
                      title={s.explanation || ""}
                    >
                      {s.explanation || (
                        <span className="italic text-gray-400">—</span>
                      )}
                    </td>
                    <td className="table-list-cell table-list-cell--actions">
                      <div className="flex justify-end gap-0.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMove(s, "UP");
                          }}
                          disabled={permLoading || !canModify || idx === 0}
                          title={!canModify ? NOT_AUTHORISED_MSG : "Move up"}
                          className="table-list-action"
                          data-testid={`BTN_control_impl_row_${s.id}_move_up`}
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMove(s, "DOWN");
                          }}
                          disabled={permLoading || !canModify || idx === steps.length - 1}
                          title={!canModify ? NOT_AUTHORISED_MSG : "Move down"}
                          className="table-list-action"
                          data-testid={`BTN_control_impl_row_${s.id}_move_down`}
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          totalRecords={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          recordLabel="steps"
        />
      </div>

      {/* Add/Edit modal */}
      <Modal isOpen={showAddModal} onClose={closeModal} size="md">
        <Modal.Header>
          {editingStep ? (
            <>
              Editing "
              <span className="item-highlight">
                Step #{editingStep.sequence_number}
              </span>
              "
            </>
          ) : (
            "Create Implementation Step"
          )}
        </Modal.Header>
        <Modal.Body>
          <form
            id="impl-step-form"
            onSubmit={handleSubmit}
            className="flex flex-col gap-y-4"
            data-testid="MOD_control_impl_form"
          >
            <FormControl.SearchSelect
              label="Type"
              required
              options={TYPES}
              value={formType}
              onChange={(v) => {
                setFormType(v as ImplementationType);
                setFormTargetId("");
              }}
              placeholder="-- Select Type --"
              data-testid="SEL_control_impl_type"
            />
            <FormControl.SearchSelect
              label="Target"
              required
              options={currentOptions.map((o) => ({
                value: o.id,
                label: o.title,
              }))}
              value={formTargetId ? Number(formTargetId) : null}
              onChange={(v) => setFormTargetId(v ? Number(v) : "")}
              placeholder="-- Select Target --"
              data-testid="SEL_control_impl_target"
            />
            <ValidatedTextarea
              label="Explanation"
              name="explanation"
              code={110}
              vh={fieldVh}
              value={formExplanation}
              onChange={(e) => setFormExplanation(e.target.value)}
              rows={4}
              placeholder="How does this target implement this control? e.g. Step automates ledger close-out via SAP F.01"
              data-testid="INP_control_impl_explanation"
            />
          </form>
        </Modal.Body>
        <Modal.Footer className={editingStep ? "app-modal-footer--split" : ""}>
          {editingStep && (
            <Button
              variant={
                editingStep.is_active === 1
                  ? "inactive-outline"
                  : "active-outline"
              }
              icon={editingStep.is_active === 1 ? CircleMinus : CircleCheck}
              onClick={handleToggleActive}
              disabled={permLoading || !canModify || toggling || submitting}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_control_impl_toggle_active"
            >
              {toggling
                ? "..."
                : editingStep.is_active === 1
                  ? "Inactive"
                  : "Active"}
            </Button>
          )}
          <Button
            type="submit"
            form="impl-step-form"
            variant={editingStep ? "update" : "fill"}
            icon={editingStep ? Check : Save}
            disabled={permLoading || !canModify || submitting || toggling}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            data-testid="BTN_control_impl_submit"
          >
            {submitting
              ? editingStep
                ? "Updating..."
                : "Submitting..."
              : editingStep
                ? "Update"
                : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default ControlImplementationsTab;
